import React, { useEffect, useState } from 'react';
import { Play, X, Volume2, Maximize2, Pause } from 'lucide-react';

/* -----------------------------------------------------------------
   Simulated demo chapters — each renders a distinct animated scene
   inside the video frame; auto-advances every ~4.5s to mimic a
   guided product tour.
----------------------------------------------------------------- */
const CHAPTERS = [
  {
    id: 'ingest',
    label: 'Signal ingest',
    duration: 4.5,
    caption: 'Predictor pulls 46M contacts + 15+ real-time signal streams.',
  },
  {
    id: 'rank',
    label: 'AI ranking',
    duration: 4.5,
    caption: 'Neural ranking scores every account against ICP + intent + timing.',
  },
  {
    id: 'accounts',
    label: 'Hot accounts',
    duration: 4.5,
    caption: '281 accounts are in-market this week. Boeing tops the list at 93.',
  },
  {
    id: 'committee',
    label: 'Buying committee',
    duration: 4.5,
    caption: 'CIO, VP Eng, and Head of Security show peak intent at Boeing.',
  },
  {
    id: 'route',
    label: 'Route & deliver',
    duration: 4.5,
    caption: 'Prioritised accounts push to Salesforce with AI-drafted outreach.',
  },
];

const TOTAL_DURATION = CHAPTERS.reduce((a, c) => a + c.duration, 0);

/* -----------------------------------------------------------------
   Scene renderers — each is a small self-contained animated SVG
----------------------------------------------------------------- */
function SceneIngest() {
  return (
    <svg viewBox="0 0 800 400" className="w-full h-full">
      <defs>
        <radialGradient id="src-glow" cx="50%" cy="50%">
          <stop offset="0%" stopColor="#4ECDC4" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#4ECDC4" stopOpacity="0" />
        </radialGradient>
      </defs>
      {/* central hub */}
      <circle cx="400" cy="200" r="90" fill="url(#src-glow)" />
      <circle cx="400" cy="200" r="42" fill="#0F1C40" stroke="#4ECDC4" strokeWidth="2" />
      <text x="400" y="196" textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="10" fill="#4ECDC4" letterSpacing="2">PREDICTOR</text>
      <text x="400" y="212" textAnchor="middle" fontFamily="Instrument Serif, serif" fontStyle="italic" fontSize="16" fill="#F1EDE4">.com</text>

      {/* source nodes streaming toward center */}
      {[
        { label: 'Bombora', x: 60, y: 60, color: '#4ECDC4' },
        { label: 'LinkedIn', x: 740, y: 70, color: '#FFB020' },
        { label: '6sense', x: 80, y: 340, color: '#4ECDC4' },
        { label: 'ZoomInfo', x: 720, y: 340, color: '#FFB020' },
        { label: 'Web signals', x: 400, y: 40, color: '#4ECDC4' },
        { label: 'CRM', x: 400, y: 360, color: '#FFB020' },
      ].map((s, i) => (
        <g key={s.label}>
          <line x1={s.x} y1={s.y} x2="400" y2="200" stroke={s.color} strokeOpacity="0.15" strokeWidth="1" strokeDasharray="3 5" />
          <rect x={s.x - 40} y={s.y - 10} width="80" height="22" rx="4" fill="#0A1024" stroke={s.color} strokeOpacity="0.4" />
          <text x={s.x} y={s.y + 4} textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="10" fill={s.color}>
            {s.label}
          </text>
          {/* traveling packet */}
          <circle r="3" fill={s.color}>
            <animate attributeName="cx" from={s.x} to="400" dur="1.6s" begin={`${i * 0.25}s`} repeatCount="indefinite" />
            <animate attributeName="cy" from={s.y} to="200" dur="1.6s" begin={`${i * 0.25}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.1;0.8;1" dur="1.6s" begin={`${i * 0.25}s`} repeatCount="indefinite" />
          </circle>
        </g>
      ))}

      <text x="400" y="290" textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="10" fill="rgba(255,255,255,0.4)" letterSpacing="3">
        128 SIGNALS / SEC
      </text>
    </svg>
  );
}

function SceneRank() {
  const rows = [
    { name: 'Boeing', score: 93, active: true },
    { name: 'AbbVie', score: 79 },
    { name: 'Baxter Healthcare', score: 79 },
    { name: 'National Australia Bank', score: 70 },
    { name: 'Lockheed Martin', score: 65 },
    { name: 'Cognizant', score: 64 },
    { name: 'Dassault Systèmes', score: 66 },
  ];
  return (
    <div className="p-8 h-full flex flex-col">
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFB020] mb-4">Neural ranking · live</div>
      <div className="grid grid-cols-2 gap-3 flex-1">
        {rows.map((r, i) => (
          <div
            key={r.name}
            className="bg-white/5 border rounded-lg p-3 flex items-center gap-3"
            style={{
              borderColor: r.active ? '#FFB020' : 'rgba(255,255,255,0.08)',
              animation: `roi-fade-in 0.35s ease ${i * 0.08}s both`,
            }}
          >
            <div
              className="w-9 h-9 rounded-md flex items-center justify-center font-serif italic text-lg tabular"
              style={{
                background: r.score > 80 ? '#FFB020' : '#4ECDC410',
                color: r.score > 80 ? '#0F1C40' : '#4ECDC4',
                border: r.score > 80 ? 'none' : '1px solid #4ECDC450',
              }}
            >
              {r.score}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] text-[#F1EDE4] font-medium truncate">{r.name}</div>
              <div className="h-1 bg-white/10 rounded-full mt-1 overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${r.score}%`, background: r.score > 80 ? '#FFB020' : '#4ECDC4' }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SceneAccounts() {
  return (
    <div className="p-8 h-full flex flex-col">
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#4ECDC4] mb-4">In-market accounts · this week</div>
      <div className="flex items-baseline gap-4 mb-6">
        <span className="text-7xl font-serif italic tabular text-[#FFB020]">281</span>
        <span className="text-white/50 text-sm">ready to engage</span>
        <span className="ml-auto text-[10px] font-mono text-[#4ECDC4]">▲ +42 this week</span>
      </div>
      <div className="grid grid-cols-3 gap-2 flex-1">
        {['Aerospace', 'Pharma', 'Defense', 'IT Services', 'Finance', 'Retail', 'Manufacturing', 'Healthcare', 'Automotive'].map((v, i) => (
          <div
            key={v}
            className="bg-white/5 border border-white/10 rounded p-2 text-[11px] flex items-center justify-between"
            style={{ animation: `roi-fade-in 0.3s ease ${i * 0.05}s both` }}
          >
            <span className="text-white/70 truncate">{v}</span>
            <span className="font-mono text-[10px]" style={{ color: i < 3 ? '#FFB020' : '#4ECDC4' }}>
              {[47, 39, 34, 28, 25, 22, 19, 18, 15][i]}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SceneCommittee() {
  const roles = [
    { role: 'CIO', score: 92, name: 'M. Zhang' },
    { role: 'VP Engineering', score: 84, name: 'D. Rossi' },
    { role: 'Director IT', score: 78, name: 'K. Okoye' },
    { role: 'Head of Security', score: 65, name: 'P. Ahuja' },
  ];
  return (
    <div className="p-8 h-full flex flex-col">
      <div className="flex items-baseline justify-between mb-5">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#4ECDC4]">Buying committee · Boeing</div>
          <div className="text-2xl font-serif italic text-[#F1EDE4] mt-1">4 personas identified</div>
        </div>
        <div className="text-[10px] font-mono text-[#FFB020]">peak intent</div>
      </div>
      <div className="space-y-3 flex-1">
        {roles.map((r, i) => (
          <div
            key={r.role}
            className="bg-white/5 border border-white/10 rounded-lg p-3 flex items-center gap-3"
            style={{ animation: `roi-slide-in 0.4s ease ${i * 0.12}s both` }}
          >
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-[13px] font-bold shrink-0"
              style={{
                background: `linear-gradient(135deg, ${r.score > 80 ? '#FFB020' : '#4ECDC4'}, #0F1C40)`,
                color: '#F1EDE4',
              }}
            >
              {r.name.split(' ').map((n) => n[0]).join('')}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline justify-between">
                <span className="text-[13px] text-[#F1EDE4]">{r.role}</span>
                <span className="text-[11px] font-mono" style={{ color: r.score > 80 ? '#FFB020' : '#4ECDC4' }}>
                  {r.score}%
                </span>
              </div>
              <div className="text-[10px] text-white/40">{r.name}</div>
              <div className="h-1 bg-white/10 rounded-full mt-1.5 overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${r.score}%`, background: r.score > 80 ? '#FFB020' : '#4ECDC4' }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SceneRoute() {
  const routes = [
    { to: 'Salesforce', status: 'Synced', color: '#4ECDC4' },
    { to: 'Slack #hot-accounts', status: 'Posted', color: '#FFB020' },
    { to: 'Outreach sequence', status: 'Queued', color: '#4ECDC4' },
    { to: 'Marketing Ops', status: 'Notified', color: '#FFB020' },
  ];
  return (
    <div className="p-8 h-full flex flex-col">
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFB020] mb-3">Route & deliver</div>
      <div className="text-2xl font-serif italic text-[#F1EDE4] mb-6">Priority accounts pushed to your stack.</div>
      <div className="grid grid-cols-2 gap-3 flex-1">
        {routes.map((r, i) => (
          <div
            key={r.to}
            className="bg-white/5 border border-white/10 rounded-lg p-4 flex flex-col justify-between"
            style={{ animation: `roi-fade-in 0.35s ease ${i * 0.1}s both` }}
          >
            <div className="text-[13px] text-[#F1EDE4] font-medium">{r.to}</div>
            <div className="flex items-center gap-2 mt-3">
              <span
                className="w-1.5 h-1.5 rounded-full pulse-dot"
                style={{ background: r.color, animationDelay: `${i * 0.15}s` }}
              />
              <span className="text-[10px] font-mono uppercase tracking-widest" style={{ color: r.color }}>
                {r.status}
              </span>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-5 pt-4 border-t border-white/10 text-[10px] font-mono uppercase tracking-widest text-white/50 flex items-center justify-between">
        <span>End-to-end latency</span>
        <span className="text-[#FFB020]">1.8s / lead</span>
      </div>
    </div>
  );
}

const SCENES = {
  ingest: SceneIngest,
  rank: SceneRank,
  accounts: SceneAccounts,
  committee: SceneCommittee,
  route: SceneRoute,
};

/* -----------------------------------------------------------------
   Modal component
----------------------------------------------------------------- */
export default function VideoDemoModal({ open, onClose }) {
  const [playing, setPlaying] = useState(true);
  const [elapsed, setElapsed] = useState(0);

  // Reset when opened
  useEffect(() => {
    if (open) {
      setElapsed(0);
      setPlaying(true);
    }
  }, [open]);

  // Playback tick
  useEffect(() => {
    if (!open || !playing) return;
    const iv = setInterval(() => {
      setElapsed((e) => {
        const next = e + 0.05;
        return next >= TOTAL_DURATION ? 0 : next; // loop
      });
    }, 50);
    return () => clearInterval(iv);
  }, [open, playing]);

  // Escape closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  // Determine active chapter
  let accum = 0;
  const activeIdx = CHAPTERS.findIndex((c) => {
    accum += c.duration;
    return elapsed < accum;
  });
  const idx = activeIdx === -1 ? CHAPTERS.length - 1 : activeIdx;
  const active = CHAPTERS[idx];
  const Scene = SCENES[active.id];
  const pct = (elapsed / TOTAL_DURATION) * 100;
  const timeStr = `${Math.floor(elapsed).toString().padStart(2, '0')}:${Math.floor((elapsed * 100) % 100)
    .toString()
    .padStart(2, '0')}`;
  const totalStr = `${Math.floor(TOTAL_DURATION).toString().padStart(2, '0')}:00`;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 lg:p-8 bg-black/85 backdrop-blur-lg"
      onClick={onClose}
      data-testid="video-demo-modal-backdrop"
    >
      <div
        className="relative w-full max-w-5xl bg-[#0A1024] rounded-2xl overflow-hidden shadow-2xl border border-white/10"
        onClick={(e) => e.stopPropagation()}
        data-testid="video-demo-modal"
      >
        {/* header bar */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 bg-[#050A1F]">
          <div className="flex items-center gap-3">
            <div className="flex gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
              <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
              <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
            </div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/60">
              Predictor.com · Product tour · 00:23
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="video-demo-close"
            className="text-white/50 hover:text-white p-1"
            aria-label="Close demo"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* video frame */}
        <div
          className="relative aspect-video bg-gradient-to-br from-[#0F1C40] via-[#0A1024] to-[#050A1F] overflow-hidden"
          data-testid="video-demo-frame"
        >
          {/* atmospheric glow */}
          <div
            className="absolute top-10 left-20 w-72 h-72 rounded-full opacity-30 pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(78,205,196,0.5), transparent 70%)' }}
          />
          <div
            className="absolute bottom-10 right-20 w-72 h-72 rounded-full opacity-30 pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(255,176,32,0.5), transparent 70%)' }}
          />

          <div key={active.id} className="absolute inset-0 pb-16 animate-in fade-in duration-500">
            <Scene />
          </div>

          {/* chapter label overlay */}
          <div className="absolute top-4 left-4 flex items-center gap-2 bg-black/50 backdrop-blur-md border border-white/10 rounded-full px-3 py-1.5 z-10">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FFB020] pulse-dot" />
            <span className="text-[10px] font-mono uppercase tracking-widest text-[#FFB020]">
              {idx + 1}/{CHAPTERS.length} · {active.label}
            </span>
          </div>

          {/* caption overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-4 lg:p-5 bg-gradient-to-t from-[#050A1F] via-[#050A1F]/85 to-transparent">
            <div className="text-sm lg:text-lg font-serif italic text-[#F1EDE4] leading-tight max-w-2xl">
              {active.caption}
            </div>
          </div>

          {!playing && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm">
              <button
                onClick={() => setPlaying(true)}
                className="w-20 h-20 rounded-full bg-[#4ECDC4] text-[#0F1C40] flex items-center justify-center shadow-[0_0_40px_rgba(78,205,196,0.6)]"
              >
                <Play className="w-8 h-8 ml-1" fill="#0F1C40" strokeWidth={0} />
              </button>
            </div>
          )}
        </div>

        {/* controls */}
        <div className="px-5 pt-3 pb-4 bg-[#050A1F]">
          {/* scrub bar */}
          <div className="relative h-1 bg-white/10 rounded-full mb-3 overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 rounded-full transition-all duration-100"
              style={{
                width: `${pct}%`,
                background: 'linear-gradient(90deg, #4ECDC4, #FFB020)',
              }}
            />
            {/* chapter markers */}
            {(() => {
              let a = 0;
              return CHAPTERS.slice(0, -1).map((c) => {
                a += c.duration;
                const p = (a / TOTAL_DURATION) * 100;
                return (
                  <div
                    key={c.id}
                    className="absolute top-0 bottom-0 w-px bg-black"
                    style={{ left: `${p}%` }}
                  />
                );
              });
            })()}
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setPlaying((p) => !p)}
                data-testid="video-demo-play-toggle"
                className="w-8 h-8 rounded-full bg-[#4ECDC4] text-[#0F1C40] flex items-center justify-center hover:bg-[#FFB020] transition-colors"
              >
                {playing ? <Pause className="w-3.5 h-3.5" fill="#0F1C40" strokeWidth={0} /> : <Play className="w-3.5 h-3.5 ml-0.5" fill="#0F1C40" strokeWidth={0} />}
              </button>
              <span className="text-[11px] font-mono text-white/60 tabular">
                {timeStr} / {totalStr}
              </span>
            </div>

            <div className="hidden md:flex items-center gap-1 flex-wrap">
              {CHAPTERS.map((c, i) => (
                <button
                  key={c.id}
                  onClick={() => {
                    let e = 0;
                    for (let j = 0; j < i; j++) e += CHAPTERS[j].duration;
                    setElapsed(e);
                  }}
                  className={`text-[9px] font-mono uppercase tracking-widest px-2 py-1 rounded transition-colors ${
                    idx === i ? 'bg-[#FFB020] text-[#0F1C40]' : 'text-white/40 hover:text-white/80'
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-3 text-white/40">
              <Volume2 className="w-3.5 h-3.5" />
              <Maximize2 className="w-3.5 h-3.5" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
